import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ToastrService from "../../services/toastService";
import Loader from "../loader/loader"
import { Logout, shouldLogout } from '../Common/Common'
import { useNavigate } from "react-router-dom";
import { useGetStepByFlowIdMutation, useGetFlowByIntentIdMutation, useAddOptionMutation, useUpdateOptionMutation, useGetActiveIntentQuery } from "./Api";

const AddOptions = ({
    mode = "add",
    onClose,
    onSave,
    data = null,
}) => {

    const [addOptionApi, { isLoading }] = useAddOptionMutation()
    const [updateOptionApi, { isLoading: updateIsLoading }] = useUpdateOptionMutation()
    const { data: activeIntents, isLoading: activeIntentsIsLoading } = useGetActiveIntentQuery()
    const [getFlowByIntentIdApi, { isLoading: getFlowIsLoading }] = useGetFlowByIntentIdMutation()
    const [getStepsByFlowIdApi, { isLoading: getStepsIsLoading }] = useGetStepByFlowIdMutation()


    const navigate = useNavigate()
    const [activeIntent, setActiveIntent] = useState([])
    const [activeFlows, setActiveFlow] = useState([])
    const [activeSteps, setActiveSteps] = useState([])
    const firstLoadIntent = useRef(true);
    const firstLoadFlow = useRef(true);


    const validationSchema = Yup.object().shape({
        intant_name: Yup.string()
            .required('Intent is required')
            .trim(),
        flow_name: Yup.string()
            .required('Flow is required')
            .trim(),
        step_name: Yup.string()
            .required('Step is required')
            .trim(),
        option_lable: Yup.string()
            .required('Option Label is required')
            .trim(),
        is_resolution: Yup.string()
            .required('Is resolution is required')
            .trim(),
        next_step: Yup.string()
            .trim()
            .when('is_resolution', {
                is: (val) => val === 'no',
                then: (schema) => schema.required('Next Step is required'),
                otherwise: (schema) => schema.notRequired(),
            }),
        resolution_text: Yup.string()
            .trim()
            .when('is_resolution', {
                is: (val) => val === 'yes',
                then: (schema) => schema.required('Resolution Text is required'),
                otherwise: (schema) => schema.notRequired(),
            }),
        status: Yup.string()
            .required('Status is required')
            .trim(),
    });

    const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'all'
    });

    const isFinalStep = watch('is_resolution');
    const isIntentSelect = watch('intant_name')
    const isFlowSelected = watch('flow_name')
    const selectedStep = watch("step_name");

    const filteredNextSteps = activeSteps?.filter(
        (item) => item.id != selectedStep
    );


    useEffect(() => {
        if (!isIntentSelect) return;

        // Skip reset during initial update load
        if (mode === "update" && firstLoadIntent.current) {
            firstLoadIntent.current = false;
            getFlowByIntentId();
            return;
        }
        // Reset dependent fields
        setValue("flow_name", "");
        setValue("step_name", "");
        setValue("next_step", "");

        setActiveFlow([]);
        setActiveSteps([]);

        // Fetch flows
        getFlowByIntentId();

    }, [isIntentSelect]);



    useEffect(() => {
        if (!isFlowSelected) return;

        // Skip reset during initial update load
        if (mode === "update" && firstLoadFlow.current) {
            firstLoadFlow.current = false;
            getStepsByFlowId();
            return;
        }

        // Reset dependent fields
        setValue("step_name", "");
        setValue("next_step", "");

        setActiveSteps([]);

        // Fetch steps
        getStepsByFlowId();

    }, [isFlowSelected]);

    useEffect(() => {
        if (data) {
            // console.log(data, "data")
            reset({
                intant_name: data.intent_id,
                flow_name: data.flow_id,
                step_name: data.step_id,
                option_lable: data.option_label,
                is_resolution: data.is_resolution == 1 ? "yes" : 'no',
                next_step: data.next_step_id,
                resolution_text: data.resolution_text,
                status: data.status,
            });
        }
    }, [])


    const resetButton = () => {
        reset({
            intant_name: "",
            flow_name: "",
            step_name: "",
            option_lable: "",
            is_resolution: "",
            next_step: "",
            resolution_text: "",
            status: "",
        });
    };

    const getFlowByIntentId = async () => {
        try {
            const response = await getFlowByIntentIdApi({ intent_id: isIntentSelect })

            if (response.data.success) {
                setActiveFlow(response?.data?.data)
            } else {
                if (shouldLogout(response.error)) {
                    Logout(navigate);
                }
            }
        } catch (err) {
            console.log(err, 'err')
        }
    }

    const getStepsByFlowId = async () => {
        try {
            const response = await getStepsByFlowIdApi({ flow_id: isFlowSelected })

            if (response.data.success) {
                setActiveSteps(response?.data?.data)
            } else {
                if (shouldLogout(response.error)) {
                    Logout(navigate);
                }
            }
        } catch (err) {
            console.log(err, 'err')
        }
    }

    const onSubmit = async (payload) => {
        try {
            if (mode == 'add') {
                const values = {
                    intent_id: payload.intant_name,
                    flow_id: payload.flow_name,
                    step_id: payload.step_name,
                    option_label: payload.option_lable,
                    is_resolution: payload.is_resolution == "yes" ? 1 : 0,
                    next_step_id: payload.is_resolution == "yes" ? null : payload.next_step,
                    resolution_text: payload.resolution_text || null,
                    status: payload.status,
                }
                console.log(values, 'values')

                const response = await addOptionApi(values)

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    reset()
                    onSave()
                } else {
                    if (shouldLogout(response.error)) {
                        Logout(navigate);
                        return
                    }
                    ToastrService.error(response.error.data.message);
                }

            } else {

                const values = {
                    intent_id: payload.intant_name,
                    flow_id: payload.flow_name,
                    step_id: payload.step_name,
                    option_label: payload.option_lable,
                    is_resolution: payload.is_resolution == "yes" ? 1 : 0,
                    next_step_id: payload.next_step,
                    resolution_text: payload.resolution_text || null,
                    status: payload.status,
                    id: data.id
                }

                const response = await updateOptionApi(values)

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    reset()
                    onSave()
                } else {
                    if (shouldLogout(response.error)) {
                        Logout(navigate);
                        return
                    }
                    ToastrService.error(response.error.data.message);
                }
            }
        } catch (err) {
            console.log(err, 'err')
        }
    }

    return (
        <div>
            {isLoading || updateIsLoading || activeIntentsIsLoading || getFlowIsLoading || getStepsIsLoading ? <Loader /> :
                <>
                    <div className="offcanvas offcanvas-end show" style={{ visibility: "visible" }} tabIndex="-1">

                        <div className="offcanvas-header px-4 py-3">

                            <div>
                                <h3 className="mb-1 drawer-header">
                                    {mode === "add" ? "Create New Options" : "Manage Options"}
                                </h3>
                                <small className="text-muted">{mode === "add" ? "Fill in the details below to create a KB Options." : "Update the details below to Manage KB Options"}</small>
                            </div>

                            <button
                                type="button"
                                className="btn-close text-reset"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>

                        </div>


                        <div className="offcanvas-body px-4 py-3">

                            <form autoComplete="off" noValidate onSubmit={handleSubmit(onSubmit)}>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Intent <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="intant_name"
                                        name="intant_name"
                                        {...register('intant_name')}
                                        className="custom-select"
                                    >
                                        <option value="">Select Intent</option>
                                        {activeIntents?.data?.length > 0 &&
                                            activeIntents?.data.map((item, index) => (
                                                <option key={index} value={item.id}>
                                                    {item.intent_name}
                                                </option>
                                            ))}
                                    </select>
                                    {errors.intant_name && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.intant_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Flow <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="flow_name"
                                        name="flow_name"
                                        {...register('flow_name')}
                                        className="custom-select"
                                        disabled={!isIntentSelect}
                                    >
                                        <option value=""> {isIntentSelect ? "Select Flow" : "Select an Intent first"}</option>
                                        {activeFlows?.length > 0 &&
                                            activeFlows?.map((item, index) => (
                                                <option key={index} value={item.id}>
                                                    {item.flow_name}
                                                </option>
                                            ))}
                                    </select>
                                    {errors.flow_name && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.flow_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Steps <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="step_name"
                                        name="step_name"
                                        {...register('step_name')}
                                        className="custom-select"
                                        disabled={!isFlowSelected}
                                    >
                                        <option value=""> {isFlowSelected ? "Select Step" : "Select a Flow first"}</option>
                                        {activeSteps?.length > 0 &&
                                            activeSteps?.map((item, index) => (
                                                <option key={index} value={item.id}>
                                                    {item.question}
                                                </option>
                                            ))}
                                    </select>
                                    {errors.step_name && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.step_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Option Label <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="option_lable"
                                        name='option_lable'
                                        placeholder='Enter the option lable'
                                        {...register('option_lable')}
                                        className="form-control"

                                    />
                                    {errors.option_lable && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.option_lable.message}
                                        </p>
                                    )}
                                </div>



                                <div className="mb-3 final-step-radio-group">
                                    <label className="form-label">
                                        Is Resolution <span className="text-danger">*</span>
                                    </label>

                                    <div className="d-flex gap-4 align-items-center">

                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                id="final_no"
                                                value="no"
                                                {...register("is_resolution")}
                                            />
                                            <label className="form-check-label small" htmlFor="final_no">
                                                No
                                            </label>
                                        </div>

                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                id="final_yes"
                                                value="yes"
                                                {...register("is_resolution")}
                                            />
                                            <label className="form-check-label small" htmlFor="final_yes">
                                                Yes
                                            </label>
                                        </div>

                                    </div>
                                    {errors.is_resolution && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.is_resolution.message}
                                        </p>
                                    )}
                                </div>

                                {isFinalStep === 'no' && (
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Next Step <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="next_step"
                                            name="next_step"
                                            {...register('next_step')}
                                            className="custom-select"
                                            disabled={!isIntentSelect}
                                        >
                                            <option value="">Select Next Step</option>
                                            {filteredNextSteps?.length > 0 &&
                                                filteredNextSteps?.map((item, index) => (
                                                    <option key={index} value={item.id}>
                                                        {item.question}
                                                    </option>
                                                ))}
                                        </select>
                                        {errors.next_step && (
                                            <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                                {errors.next_step.message}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {isFinalStep === 'yes' && (
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Resolution Text <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="resolution_text"
                                            name='resolution_text'
                                            placeholder='Enter the resolution text'
                                            {...register('resolution_text')}
                                            className="form-control"

                                        />
                                        {errors.resolution_text && (
                                            <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                                {errors.resolution_text.message}
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">
                                        Status <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="status"
                                        name="status"
                                        {...register('status')}
                                        className="custom-select"
                                    >
                                        <option value="">Select status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                    {errors.status && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.status.message}
                                        </p>
                                    )}

                                </div>

                                <div className="d-flex gap-2 mt-4">
                                    <button type="submit" className="btn btn-primary">
                                        {mode === "add" ? "Create Option" : "Update Option"}
                                    </button>

                                    {mode === "add" && (
                                        <button type="button" className="btn btn-secondary" onClick={resetButton}>
                                            Reset
                                        </button>
                                    )}
                                </div>

                            </form>

                        </div>
                    </div>

                    {/* Backdrop */}
                    <div className="offcanvas-backdrop fade show" onClick={onClose}></div>

                </>
            }
        </div>

    );
};

export default AddOptions;